import React, { useState, useEffect, useRef, useCallback, useContext, useMemo } from 'react';
import { ColorType, createChart } from 'lightweight-charts';
import * as api from '../../utils/api';
import styles from './TradePage.module.css';

import { ThemeContext } from '../../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { usePrice } from '../../context/PriceContext';
import { FiSearch, FiChevronDown } from 'react-icons/fi';

const darkThemeColors = {
    backgroundColor: '#1A202C',
    textColor: '#E2E8F0',
    gridColor: 'rgba(74, 85, 104, 0.5)',
    positiveColor: '#48BB78',
    negativeColor: '#FC8181',
};

const lightThemeColors = {
    backgroundColor: '#F7FAFC',
    textColor: '#2D3748',
    gridColor: 'rgba(226, 232, 240, 0.7)',
    positiveColor: '#38A169',
    negativeColor: '#E53E3E',
};

const initializeChart = (containerElement, colors) => {
  const chart = createChart(containerElement, {
    layout: {
      background: {
        type: ColorType.Solid,
        color: colors.backgroundColor,
      },
      textColor: colors.textColor,
    },
    grid: {
      vertLines: { color: colors.gridColor },
      horzLines: { color: colors.gridColor },
    },
    timeScale: {
      timeVisible: true,
      secondsVisible: false,
    },
  });

  return chart;
};

const addCandlestickSeries = (chart, colors) => {
  return chart.addCandlestickSeries({
    upColor: colors.positiveColor,
    downColor: colors.negativeColor,
    borderDownColor: colors.negativeColor,
    borderUpColor: colors.positiveColor,
    wickDownColor: colors.negativeColor,
    wickUpColor: colors.positiveColor,
  });
};

const convertToIST = (time) => {
  return time + 5.5 * 60 * 60;
};

const TradePage = () => {
  const [settings, setSettings] = useState({
    chartSymbol: '',
    chartTime: '1m',
    chartType: 'Candle',
  });
  const chartContainerRef = useRef();
  const [isLoading, setIsLoading] = useState(true);
  const [allSymbols, setAllSymbols] = useState([]); 
  const [scripDetails, setScripDetails] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  
  const timeframes = ['1m', '5m', '15m', '1h', '4h', '1d'];
  const chartTypes = ['Candle', 'Line', 'Bar'];
  const navigate = useNavigate();
  const lastCandleRef = useRef(null);
  const seriesRef = useRef(null);

  const { price: currentPrice } = usePrice(settings.chartSymbol);

  const [showTimeframeMenu, setShowTimeframeMenu] = useState(false);
  const [showChartTypeMenu, setShowChartTypeMenu] = useState(false);
  const timeframeMenuRef = useRef(null);
  const chartTypeMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (timeframeMenuRef.current && !timeframeMenuRef.current.contains(event.target)) {
        setShowTimeframeMenu(false);
      }
      if (chartTypeMenuRef.current && !chartTypeMenuRef.current.contains(event.target)) {
        setShowChartTypeMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch all symbols for search (Aligned with old_frontend)
  useEffect(() => {
    const fetchAllSymbols = async () => {
        try {
            const { data } = await api.getSymbols();
            setAllSymbols(data);
        } catch (e) {}
    };
    fetchAllSymbols();
  }, []);

  useEffect(() => {
    if (!settings.chartSymbol) return;
    const match = allSymbols.find(s => s.user_symbol === settings.chartSymbol);
    if (match) setScripDetails(match);
  }, [settings.chartSymbol, allSymbols]);

  const isIndex = useMemo(() => {
    return scripDetails?.symbol_type === 'Index';
  }, [scripDetails]);

  const handleSettingsChange = useCallback(async (newSetting) => {
    const updatedSettings = { ...settings, ...newSetting };
    setSettings(updatedSettings);
    try { await api.updateChartSettings(newSetting); } catch (error) {}
  }, [settings]);

  const handleSymbolSelect = (symbol) => {
    handleSettingsChange({ chartSymbol: symbol });
    setSearchTerm('');
    setIsFocused(false);
  }

  const searchResults = useMemo(() => {
    if (!searchTerm.trim()) return [];
    return allSymbols.filter(s => 
        s.user_symbol.toLowerCase().includes(searchTerm.toLowerCase())
    ).slice(0, 10);
  }, [searchTerm, allSymbols]);

  useEffect(() => {
    const fetchChartSettings = async () => {
      try {
        const { data } = await api.getChartSettings();
        if (data) {
          setSettings({
            chartSymbol: data.chartSymbol || 'NIFTY',
            chartTime: data.chartTime || '1m',
            chartType: data.chartType || 'Candle',
          });
        }
      } catch (error) {}
      setIsLoading(false);
    };
    fetchChartSettings();
  }, []);

  const { theme } = useContext(ThemeContext);

  useEffect(() => {
    if (isLoading || !settings.chartSymbol || !chartContainerRef.current) return;

    chartContainerRef.current.innerHTML = '';
    const chartColors = theme === 'dark' ? darkThemeColors : lightThemeColors;
    const chart = initializeChart(chartContainerRef.current, chartColors);
    let series = null;

    if (settings.chartType === 'Line') {
      series = chart.addLineSeries({ color: '#2962FF' });
    } else if (settings.chartType === 'Bar') {
      series = chart.addBarSeries({ upColor: '#26a69a', downColor: '#ef5350' });
    } else {
      series = addCandlestickSeries(chart, chartColors);
    }
    seriesRef.current = series;

    const priceApiUrl = process.env.REACT_APP_PRICE_API_URL;
    const apiKey = process.env.REACT_APP_PRICE_API_KEY;

    const intervalMap = {
        '1m': '1', '5m': '5', '15m': '15', '1h': '60', '4h': '240', '1d': '1D'
    };

    const fetchHistorical = async () => {
        try {
            const res = await axios.get(`${priceApiUrl}/api/historical/`, {
                params: {
                    symbols: settings.chartSymbol,
                    apiKey: apiKey,
                    interval: intervalMap[settings.chartTime] || '1',
                    bars: 200
                }
            });
            
            // Check if chart still exists (hasn't been removed by cleanup)
            if (!chartContainerRef.current || !seriesRef.current) return;

            if (res.data && res.data[settings.chartSymbol] && Array.isArray(res.data[settings.chartSymbol].data)) {
                // Aligned with old_frontend: convert to IST and format
                const formatted = res.data[settings.chartSymbol].data.map(d => ({
                    time: convertToIST(d.time),
                    open: d.open, 
                    high: d.high, 
                    low: d.low, 
                    close: d.close
                })).sort((a, b) => a.time - b.time);
                
                if (formatted.length > 0) {
                    if (settings.chartType === 'Line') {
                        series.setData(formatted.map(d => ({ time: d.time, value: d.close })));
                    } else {
                        series.setData(formatted);
                    }
                    lastCandleRef.current = formatted[formatted.length - 1];
                    
                    chart.timeScale().setVisibleRange({
                        from: (formatted.length > 100 ? formatted[formatted.length - 100].time : formatted[0].time),
                        to: formatted[formatted.length - 1].time,
                    });
                }
            }
        } catch (e) { console.error("Historical fetch error:", e); }
    };
    fetchHistorical();

    return () => { if (chart) chart.remove(); seriesRef.current = null; };
  }, [settings, isLoading, theme]);

  // Handle real-time updates from usePrice (Aligned with old_frontend nested structure)
  // Actually PriceContext might already be parsing it, let's assume usePrice handles the socket data.
  useEffect(() => {
    if (currentPrice && lastCandleRef.current && seriesRef.current) {
        // PriceContext might give price directly or as a string if market closed
        if (typeof currentPrice !== 'number') return;

        const timeframeMap = { '1m': 60, '5m': 300, '15m': 900, '1h': 3600, '4h': 14400, '1d': 86400 };
        const seconds = timeframeMap[settings.chartTime] || 60;
        const currentTime = Math.floor(Date.now() / 1000);
        const candleTime = convertToIST(Math.floor(currentTime / seconds) * seconds);
        
        let updatedCandle;
        if (candleTime > lastCandleRef.current.time) {
            updatedCandle = { time: candleTime, open: currentPrice, high: currentPrice, low: currentPrice, close: currentPrice };
        } else {
            updatedCandle = { 
                ...lastCandleRef.current, 
                high: Math.max(lastCandleRef.current.high, currentPrice), 
                low: Math.min(lastCandleRef.current.low, currentPrice), 
                close: currentPrice 
            };
        }
        
        if (settings.chartType === 'Line') {
            seriesRef.current.update({ time: updatedCandle.time, value: updatedCandle.close });
        } else {
            seriesRef.current.update(updatedCandle);
        }
        lastCandleRef.current = updatedCandle;
    }
  }, [currentPrice, settings.chartTime, settings.chartType]);

  const handleBuySellClick = async (tradeType) => {
    navigate(`/order/${settings.chartSymbol}/${tradeType}`, { state: { livePrice: currentPrice } });
  };

  const formatCurrency = (val) => {
    if (val == null || typeof val !== 'number') return '--';
    return val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const priceChange = useMemo(() => {
    if (typeof currentPrice !== 'number' || !lastCandleRef.current?.open) return 0;
    return currentPrice - lastCandleRef.current.open;
  }, [currentPrice]);

  const priceChangePercent = useMemo(() => {
    if (typeof currentPrice !== 'number' || !lastCandleRef.current?.open) return 0;
    return (priceChange / lastCandleRef.current.open) * 100;
  }, [currentPrice, priceChange]);

  return (
    <div className={styles['trade-page']}>
      <div className={styles['trade-header']}>
        <div className={styles['chart-controls']}>
            <div className={styles['search-container']}>
            <FiSearch className={styles['search-icon']} />
            <input
                type="text"
                value={isFocused ? searchTerm : settings.chartSymbol}
                onFocus={() => { setIsFocused(true); setSearchTerm(''); }}
                onBlur={() => { setTimeout(() => setIsFocused(false), 200); }}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search symbol..."
                className={styles['search-input']}
            />
            {isFocused && searchResults.length > 0 && (
                <div className={styles['search-results']}>
                {searchResults.map(s => (
                    <div key={`${s.user_symbol}-${s.sr_no}`} className={styles['search-result-item']} onMouseDown={() => handleSymbolSelect(s.user_symbol)}>
                    <span className={styles['res-symbol']}>{s.user_symbol}</span>
                    <span className={styles['res-exch']}>{s.official_symbol?.split(':')[0]}</span>
                    </div>
                ))}
                </div>
            )}
            </div>
            
            <div className={styles['controls-right']}>
                <div className={styles['custom-dropdown']} ref={timeframeMenuRef}>
                    <div className={styles['dropdown-trigger']} onClick={() => setShowTimeframeMenu(!showTimeframeMenu)}>
                        <span>{settings.chartTime.toUpperCase()}</span>
                        <FiChevronDown className={`${styles['chevron']} ${showTimeframeMenu ? styles['rotate'] : ''}`} />
                    </div>
                    {showTimeframeMenu && (
                        <div className={styles['dropdown-menu']}>
                            {timeframes.map(tf => (
                                <div
                                    key={tf}
                                    className={`${styles['select-item']} ${settings.chartTime === tf ? styles['selected-opt'] : ''}`}
                                    onClick={() => { handleSettingsChange({ chartTime: tf }); setShowTimeframeMenu(false); }}
                                >
                                    {tf.toUpperCase()}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className={styles['custom-dropdown']} ref={chartTypeMenuRef}>
                    <div className={styles['dropdown-trigger']} onClick={() => setShowChartTypeMenu(!showChartTypeMenu)}>
                        <span>{settings.chartType}</span>
                        <FiChevronDown className={`${styles['chevron']} ${showChartTypeMenu ? styles['rotate'] : ''}`} />
                    </div>
                    {showChartTypeMenu && (
                        <div className={styles['dropdown-menu']}>
                            {chartTypes.map(type => (
                                <div
                                    key={type}
                                    className={`${styles['select-item']} ${settings.chartType === type ? styles['selected-opt'] : ''}`}
                                    onClick={() => { handleSettingsChange({ chartType: type }); setShowChartTypeMenu(false); }}
                                >
                                    {type}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>

        <div className={styles['info-bar']}>
            <div className={styles['price-main']}>
                <span className={styles['ltp']}>₹{formatCurrency(currentPrice)}</span>
                <span className={`${styles['change']} ${priceChange >= 0 ? styles.positive : styles.negative}`}>
                    {priceChange >= 0 ? '+' : ''}{formatCurrency(priceChange)} ({priceChangePercent.toFixed(2)}%)
                </span>
            </div>
            <div className={styles['ohlc-data']}>
                <div className={styles['ohlc-item']}><span>O</span> {formatCurrency(lastCandleRef.current?.open)}</div>
                <div className={styles['ohlc-item']}><span>H</span> {formatCurrency(lastCandleRef.current?.high)}</div>
                <div className={styles['ohlc-item']}><span>L</span> {formatCurrency(lastCandleRef.current?.low)}</div>
                <div className={styles['ohlc-item']}><span>C</span> {formatCurrency(lastCandleRef.current?.close)}</div>
            </div>
        </div>
      </div>

      <div className={styles['chart-wrapper']} ref={chartContainerRef} />
      
      <div className={styles['action-footer']}>
        <button className={`${styles['btn-action']} ${styles.buy}`} onClick={() => handleBuySellClick('buy')} disabled={isIndex}>
            BUY
        </button>
        <button className={`${styles['btn-action']} ${styles.sell}`} onClick={() => handleBuySellClick('sell')} disabled={isIndex}>
            SELL
        </button>
      </div>
    </div>
  );
};

export default TradePage;
